import { useState, useCallback } from "react";
import {
    FileJson,
    Hash,
    Image as ImageIcon,
    LayoutGrid,
    Type,
} from "lucide-react";
import { useAsciiArt } from "./AsciiArtContext";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";

const COMMON_FONTS = [
    "monospace",
    "serif",
    "sans-serif",
    "Arial",
    "Times New Roman",
    "Courier New",
    "Georgia",
    "Verdana",
    "Helvetica",
];

const hasLocalFontAccess = "queryLocalFonts" in window;

const RAMP_PRESETS: { label: string; value: string }[] = [
    {
        label: "Full (70 chars)",
        value: " .'`^\",:;Il!i><~+_-?][}{1)(|\\/tfjrxnuvczXYUJCLQ0OZmwqpdbkhao*#MW&8%B@$",
    },
    { label: "Standard", value: " .:-=+*#%@" },
    { label: "Blocks", value: " ░▒▓█" },
    { label: "Minimal", value: " .:*#" },
    { label: "Dot matrix", value: " .·•●" },
    { label: "Numbers", value: " 1234567890" },
    { label: "Dense", value: " .,;:!|/(#@" },
    { label: "Binary", value: " █" },
    { label: "Custom", value: "" },
];

export function AsciiArtControls() {
    const {
        imageName,
        cols,
        rows,
        cellWidth,
        cellHeight,
        font,
        fontSize,
        noiseAmount,
        noiseScale,
        seed,
        invert,
        characterRamp,
        lockAspectRatio,
        imageAspectRatio,
        setCols,
        setRows,
        setCellWidth,
        setCellHeight,
        setFont,
        setFontSize,
        setNoiseAmount,
        setNoiseScale,
        setSeed,
        setInvert,
        setCharacterRamp,
        setLockAspectRatio,
        handleImageLoad,
        handleJsonImport,
        imageInputRef,
        jsonInputRef,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useAsciiArt();

    const [systemFonts, setSystemFonts] = useState<string[] | null>(null);
    const [fontAccessError, setFontAccessError] = useState<string | null>(null);

    const loadSystemFonts = useCallback(async () => {
        try {
            const fonts: FontData[] = await (window as any).queryLocalFonts();
            const families = new Set<string>();
            for (const f of fonts) {
                families.add(f.family);
            }
            const sorted = Array.from(families).sort((a, b) =>
                a.localeCompare(b, undefined, { sensitivity: "base" }),
            );
            setSystemFonts(sorted);
            setFontAccessError(null);
        } catch (err) {
            if (err instanceof DOMException && err.name === "NotAllowedError") {
                setFontAccessError("Permission denied.");
            } else {
                setFontAccessError("Failed to load fonts.");
            }
        }
    }, []);

    const fontOptions = systemFonts
        ? [
              ...COMMON_FONTS,
              ...systemFonts.filter((f) => !COMMON_FONTS.includes(f)),
          ]
        : COMMON_FONTS;

    const handlePositiveInt = (setter: (v: number) => void, max = 500) => {
        return (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < 1 || n > max) return;
            setter(Math.round(n));
        };
    };

    const handlePositiveFloat =
        (setter: (v: number) => void, min = 0.001, max = 1000) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(n);
        };

    return (
        <div className="panel-controls ascii-art-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>ASCII Art</h2>
                    <ToolDescriptionPopover dialogLabel="ASCII Art tool description">
                        <p>
                            Convert a raster image into an SVG grid of ASCII
                            characters, mapping pixel brightness to character
                            density.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>
            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="ascii-art"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />
                <CollapsibleToolGroup title="Image" icon={ImageIcon}>
                    <label className="field-label">Image File</label>
                    <div className="upload-controls">
                        <button
                            type="button"
                            className="generic-button"
                            onClick={() => imageInputRef.current?.click()}
                        >
                            Choose image file...
                        </button>
                        <input
                            ref={imageInputRef}
                            type="file"
                            accept="image/*"
                            className="upload-input"
                            onChange={handleImageLoad}
                        />
                    </div>
                    {imageName && (
                        <p className="upload-info">
                            Loaded: <strong>{imageName}</strong>
                        </p>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Grid & cells" icon={LayoutGrid}>
                    <div className="field-row">
                        <CheckboxField
                            checked={lockAspectRatio}
                            onChange={setLockAspectRatio}
                            label="Lock columns to image aspect ratio"
                            inline
                            disabled={imageAspectRatio === null}
                        />
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Columns{lockAspectRatio ? " (auto)" : ""}
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={500}
                                step={1}
                                value={cols}
                                onChange={handlePositiveInt(setCols)}
                                debounceMs={300}
                                disabled={lockAspectRatio}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Rows
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={500}
                                step={1}
                                value={rows}
                                onChange={handlePositiveInt(setRows)}
                                debounceMs={300}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Cell Width (SVG units)
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={100}
                                step={1}
                                value={cellWidth}
                                onChange={handlePositiveInt(setCellWidth, 100)}
                                debounceMs={300}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Cell Height (SVG units)
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={100}
                                step={1}
                                value={cellHeight}
                                onChange={handlePositiveInt(setCellHeight, 100)}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Typography & noise" icon={Type}>
                    <div className="field-row">
                        <label className="field-label">
                            Font
                            <select
                                value={font}
                                onChange={(e) => setFont(e.target.value)}
                                className="font-select"
                            >
                                {fontOptions.map((f) => (
                                    <option
                                        key={f}
                                        value={f}
                                        style={{ fontFamily: f }}
                                    >
                                        {f}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="text"
                                value={font}
                                onChange={(e) => setFont(e.target.value)}
                                placeholder="Custom font name"
                                className="font-input"
                            />
                        </label>
                        {hasLocalFontAccess && !systemFonts && (
                            <button
                                type="button"
                                className="generic-button text-noise-fill-load-fonts-btn"
                                onClick={loadSystemFonts}
                            >
                                Load system fonts
                            </button>
                        )}
                        {systemFonts && (
                            <p className="upload-info">
                                {systemFonts.length} system font
                                {systemFonts.length === 1 ? "" : "s"} loaded.
                            </p>
                        )}
                        {fontAccessError && (
                            <p className="upload-error">{fontAccessError}</p>
                        )}
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Font Size
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={100}
                                step={1}
                                value={fontSize}
                                onChange={handlePositiveInt(setFontSize, 100)}
                                debounceMs={300}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Noise Amount (0-1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.01}
                                value={noiseAmount}
                                onChange={handlePositiveFloat(
                                    setNoiseAmount,
                                    0,
                                    1,
                                )}
                                debounceMs={300}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Noise Scale
                            <DebouncedInput
                                type="number"
                                min={0.001}
                                max={10}
                                step={0.01}
                                value={noiseScale}
                                onChange={handlePositiveFloat(
                                    setNoiseScale,
                                    0.001,
                                    10,
                                )}
                                debounceMs={300}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Seed
                            <input
                                type="text"
                                value={seed}
                                onChange={(e) => setSeed(e.target.value)}
                                placeholder="Enter seed text"
                                className="seed-input"
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <CheckboxField
                            checked={invert}
                            onChange={setInvert}
                            label="Invert brightness"
                            inline
                        />
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Character ramp" icon={Hash}>
                    <div className="field-row">
                        <label className="field-label">
                            Character Ramp Preset
                            <select
                                value={
                                    RAMP_PRESETS.find(
                                        (p) => p.value === characterRamp,
                                    )
                                        ? characterRamp
                                        : ""
                                }
                                onChange={(e) => {
                                    if (e.target.value !== "") {
                                        setCharacterRamp(e.target.value);
                                    }
                                }}
                            >
                                {RAMP_PRESETS.map((p) => (
                                    <option key={p.label} value={p.value}>
                                        {p.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Character Ramp (light → dark)
                            <input
                                type="text"
                                value={characterRamp}
                                onChange={(e) =>
                                    setCharacterRamp(e.target.value)
                                }
                                placeholder=" .:-=+*#%@"
                                className="ramp-input"
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Import JSON" icon={FileJson}>
                    <label className="field-label">
                        Import Character Set JSON
                    </label>
                    <div className="upload-controls">
                        <button
                            type="button"
                            className="generic-button"
                            onClick={() => jsonInputRef.current?.click()}
                        >
                            Choose JSON file...
                        </button>
                        <input
                            ref={jsonInputRef}
                            type="file"
                            accept=".json,application/json"
                            className="upload-input"
                            onChange={handleJsonImport}
                        />
                    </div>
                    <p className="upload-info">
                        Format: <code>{`{ "characters": " .:-=+*#%@" }`}</code>
                    </p>
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
